import { lazy, Suspense } from 'react'
import { BrowserRouter, Outlet, Route, Routes } from 'react-router-dom'
import { AuthProvider, useAuth } from './auth/AuthContext'
import { Shell } from './components/Shell'
import { Applications } from './screens/Applications'
import { Journal } from './screens/Journal'
import { Login } from './screens/Login'
import { Moderators } from './screens/Moderators'
import { Users } from './screens/Users'
import { Overview } from './screens/Overview'
import { Queue } from './screens/Queue'
import { ReportDetail } from './screens/ReportDetail'
import { Settings } from './screens/Settings'
import { CardSkeleton, EmptyState } from './components/ui'

/**
 * Statistics pulls in the whole charting library, which is roughly two thirds
 * of the bundle and is only needed on one screen. Split out so the queue — the
 * screen moderators actually live in — loads without it.
 */
const Stats = lazy(() =>
  import('./screens/Stats').then((module) => ({ default: module.Stats })),
)

/**
 * Routes, named in Azerbaijani so a URL is readable to the people using it.
 *
 * Screen IDs: 7a icmal (index), 7b novbe, 7c novbe/:id, 7d moderatorlar,
 * 7e jurnal, 7f muracietler, 8h statistika.
 */
export function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          <Route element={<RequireStaff />}>
            <Route element={<Shell />}>
              <Route index element={<Overview />} />
              <Route
                path="statistika"
                element={
                  <Suspense fallback={<CardSkeleton height={420} />}>
                    <Stats />
                  </Suspense>
                }
              />
              <Route path="novbe" element={<Queue />} />
              <Route path="novbe/:reportId" element={<ReportDetail />} />
              <Route path="istifadeciler" element={<Users />} />
              <Route path="moderatorlar" element={<Moderators />} />
              <Route path="muracietler" element={<Applications />} />
              <Route path="jurnal" element={<Journal />} />
              <Route path="parametrler" element={<Settings />} />
              <Route
                path="*"
                element={
                  <EmptyState
                    title="Səhifə tapılmadı"
                    description="Bu ünvanda heç nə yoxdur."
                  />
                }
              />
            </Route>
          </Route>
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  )
}

/**
 * Gate for the whole panel.
 *
 * Renders nothing but a mark while the stored session is being checked, so an
 * authenticated reload does not flash the login screen. Signing out swaps the
 * tree back to <Login /> without a redirect, which keeps the deep link the user
 * arrived on intact.
 */
function RequireStaff() {
  const { user, loading } = useAuth()

  if (loading) {
    return (
      <div className="boot" aria-busy="true" aria-label="Yüklənir">
        <span className="boot__mark">SB</span>
      </div>
    )
  }

  if (!user) return <Login />

  return <Outlet />
}
